import type { DiffNavigationHandlers } from '@/lib/stores/navigation/diff-navigation-store';
import type { CodeContent } from '@/types/github';

import { findMatchingFilename, isPlausibleSymbol } from './find-symbol-definition';
import { navigateToFileAndLine, navigateToSymbolDefinition } from './navigate-to-symbol';

type SymbolLocationMap = Record<string, { file: string; line: number }>;

export type SymbolReferenceNavigationResult =
  | 'filename'
  | 'symbol-location'
  | 'symbol-search'
  | 'none';

type NavigateSymbolReferenceParams = {
  text: string;
  allCodeContent: CodeContent[];
  navigateToFile: DiffNavigationHandlers['navigateToFile'];
  preferredFiles?: CodeContent[];
  symbolMap?: SymbolLocationMap;
  originElementId?: string;
  allowFilenameMatch?: boolean;
};

export function canNavigateSymbolReference(
  text: string,
  allCodeContent: CodeContent[],
  options: { allowFilenameMatch?: boolean } = {},
): boolean {
  const symbolText = text.trim();
  if (!symbolText || allCodeContent.length === 0) {
    return false;
  }

  if (options.allowFilenameMatch && findMatchingFilename(symbolText, allCodeContent)) {
    return true;
  }

  return isPlausibleSymbol(symbolText);
}

export function navigateSymbolReference({
  allowFilenameMatch = false,
  allCodeContent,
  navigateToFile,
  originElementId,
  preferredFiles,
  symbolMap,
  text,
}: NavigateSymbolReferenceParams): SymbolReferenceNavigationResult {
  const symbolText = text.trim();
  if (!symbolText || allCodeContent.length === 0) {
    return 'none';
  }

  if (allowFilenameMatch) {
    const matchedFilename = findMatchingFilename(symbolText, allCodeContent);
    if (matchedFilename) {
      navigateToFile(matchedFilename, { expandFile: true, source: 'programmatic' });
      return 'filename';
    }
  }

  if (!isPlausibleSymbol(symbolText)) {
    return 'none';
  }

  const symbolLocation = symbolMap?.[symbolText];
  if (
    symbolLocation &&
    typeof symbolLocation.file === 'string' &&
    typeof symbolLocation.line === 'number' &&
    symbolLocation.line > 0 &&
    allCodeContent.some((candidateFile) => candidateFile.filename === symbolLocation.file)
  ) {
    navigateToFileAndLine(
      symbolLocation.file,
      symbolLocation.line,
      navigateToFile,
      'new',
      originElementId,
    );
    return 'symbol-location';
  }

  return navigateToSymbolDefinition(
    symbolText,
    allCodeContent,
    navigateToFile,
    preferredFiles,
    originElementId,
  )
    ? 'symbol-search'
    : 'none';
}
